import { useState, useEffect, useRef } from 'react'
import { useGameStore } from '@store/gameStore'
import { GAME_CONFIG } from '@config/gameConfig'
import { GameLayout } from '@components/GameLayout'
import { SecondChanceModal } from '@components/SecondChanceModal'
import './ShopScreen.css'


// VIP Icons based on level
const getVIPIcon = (level: number) => {
    if (level === 1) return '👑'
    return `👑${level}` // Crown with level number
}

// Bundle definitions
// Starter Pack Progression Chain
const STARTER_CHAIN = [
    { level: 0, price: 19.99, gems: 1000, gold: 5000, item: { name: 'Seltenes Schwert', power: 30, slot: 'weapon', rarity: 'rare' } },
    { level: 1, price: 29.99, gems: 2000, gold: 15000, item: { name: 'Heldenhaube', power: 40, slot: 'helmet', rarity: 'rare' } },
    { level: 2, price: 39.99, gems: 3500, gold: 30000, item: { name: 'Ritterrüstung', power: 55, slot: 'armor', rarity: 'rare' } },
    { level: 3, price: 49.99, gems: 5000, gold: 50000, item: { name: 'Königsschild', power: 70, slot: 'weapon', rarity: 'epic' } },
    { level: 4, price: 59.99, gems: 7000, gold: 80000, item: { name: 'Drachentöter', power: 90, slot: 'weapon', rarity: 'epic' } },
    { level: 5, price: 69.99, gems: 9000, gold: 120000, item: { name: 'Titanenhelm', power: 110, slot: 'helmet', rarity: 'epic' } },
    { level: 6, price: 79.99, gems: 12000, gold: 180000, item: { name: 'Götterrüstung', power: 130, slot: 'armor', rarity: 'legendary' } },
    { level: 7, price: 89.99, gems: 15000, gold: 250000, item: { name: 'Donnerklinge', power: 150, slot: 'weapon', rarity: 'legendary' } },
    { level: 8, price: 99.99, gems: 20000, gold: 350000, item: { name: 'Zeus Krone', power: 180, slot: 'helmet', rarity: 'legendary' } },
    { level: 9, price: 119.99, gems: 30000, gold: 500000, item: { name: 'Ewige Flamme', power: 220, slot: 'weapon', rarity: 'legendary' } },
]

// Mega Bundle Regression (Getting worse)
const MEGA_LEVELS = [
    { level: 0, price: 49.99, gems: 10000, gold: 0, vipDays: 3, items: 3, label: '🔥 MEGA BUNDLE' },
    { level: 1, price: 49.99, gems: 8000, gold: 5000, vipDays: 2, items: 2, label: '🔥 MEGA DEAL II' }, // Worse value
    { level: 2, price: 49.99, gems: 6000, gold: 10000, vipDays: 1, items: 2, label: '🔥 MEGA DEAL III' },
    { level: 3, price: 49.99, gems: 4000, gold: 20000, vipDays: 1, items: 1, label: '🔥 MEGA DEAL IV' },
    // Final Stage: Infinitely purchasable, bad value compared to start
    { level: 4, price: 29.99, gems: 1000, gold: 0, vipDays: 1, items: 1, label: '🔥 ENDLESS GRIND', infinite: true },
]

// Regular bundles (non-special)
const REGULAR_BUNDLES = [
    {
        id: 'power',
        name: 'Power Bundle',
        price: 4.99,
        originalPrice: 9.99,
        rarity: 'rare',
        rewards: { gems: 500, energy: 100 },
        items: [
            { emoji: '💎', label: '500 Gems' },
            { emoji: '⚡', label: '100 Energie' },
        ],
    },
    {
        id: 'wealth',
        name: 'Reichtums-Paket',
        price: 7.99,
        originalPrice: 15.99,
        rarity: 'rare',
        rewards: { gold: 10000, gems: 300 },
        items: [
            { emoji: '💰', label: '10.000 Gold' },
            { emoji: '💎', label: '300 Gems' },
        ],
    },
    {
        id: 'lucky',
        name: 'Glückspaket',
        price: 9.99,
        originalPrice: 19.99,
        rarity: 'epic',
        rewards: { lootboxes: 3, gems: 200 },
        items: [
            { emoji: '🎁', label: '3 Lootboxen' },
            { emoji: '💎', label: '200 Gems' },
        ],
        freeLootboxSpins: 3,
    },
    {
        id: 'energy',
        name: 'Energie-Boost',
        price: 5.99,
        originalPrice: 8.99,
        rarity: 'rare',
        rewards: { energy: 200, gold: 5000 },
        items: [
            { emoji: '⚡', label: '200 Energie' },
            { emoji: '💰', label: '5.000 Gold' },
        ],
    },
    {
        id: 'jackpot',
        name: 'Jackpot Mega',
        price: 14.99,
        originalPrice: 29.99,
        rarity: 'legendary',
        rewards: { gems: 1500, gold: 20000, energy: 150 },
        items: [
            { emoji: '💎', label: '1.500 Gems' },
            { emoji: '💰', label: '20.000 Gold' },
            { emoji: '⚡', label: '150 Energie' },
        ],
    },
    {
        id: 'lootboxes',
        name: 'Lootbox Bonanza',
        price: 12.99,
        originalPrice: 24.99,
        rarity: 'epic',
        rewards: { lootboxes: 5, gems: 500 },
        items: [
            { emoji: '🎁', label: '5 Lootboxen' },
            { emoji: '💎', label: '500 Gems' },
        ],
        freeLootboxSpins: 5,
    },
    // Tutorial Free Bundle (only visible during tutorial)
    {
        id: 'tutorial-free',
        name: 'Willkommensgeschenk',
        price: 0,
        originalPrice: 9.99,
        rarity: 'rare',
        rewards: { lootboxes: 1 },
        items: [
            { emoji: '🎁', label: '1 Gratis Lootbox' },
            { emoji: '✨', label: 'Tutorial Spezial' },
        ],
        freeLootboxSpins: 1,
        tutorialOnly: true,
    },
]

const GEM_PACKS = [
    { gems: 100, price: 0.99, bonus: '', popular: false },
    { gems: 500, price: 4.99, bonus: '', popular: false },
    { gems: 1200, price: 9.99, bonus: '+20%', popular: false },
    { gems: 2800, price: 19.99, bonus: '+40%', popular: false },
    { gems: 6500, price: 49.99, bonus: '+80%', popular: true },
    { gems: 15000, price: 99.99, bonus: '+150%', popular: false },
]

const ENERGY_PACKS = [
    { energy: 50, price: 100, bonus: '', popular: false },
    { energy: 150, price: 200, bonus: '', popular: true },
    { energy: 300, price: 300, bonus: '', popular: false },
]

const HERO_SKINS = [
    { id: 'default', name: 'Rookie', price: 0, image: '/rookie.png', rarity: 'common' },
    { id: 'knight', name: 'Ritter', price: 9.99, image: '/ritter.png', rarity: 'rare' },
    { id: 'king', name: 'König', price: 9.99, image: '/king.png', rarity: 'legendary' },
    { id: 'wizard', name: 'Magier', price: 9.99, image: '/magier.png', rarity: 'legendary' },
]

const EMPIRE_COLORS = [
    { id: 'blue', value: '#3b82f6', name: 'Königsblau', price: 0 },
    { id: 'red', value: '#ef4444', name: 'Rubinrot', price: 4.99 },
    { id: 'green', value: '#10b981', name: 'Smaragdgrün', price: 4.99 },
    { id: 'purple', value: '#8b5cf6', name: 'Amethyst', price: 4.99 },
    { id: 'gold', value: '#f59e0b', name: 'Gold', price: 4.99 },
]

const EMPIRE_EMBLEMS = [
    { id: 'castle', value: '🏰', name: 'Burg', price: 0 },
    { id: 'sword', value: '⚔️', name: 'Schwerter', price: 4.99 },
    { id: 'dragon', value: '🐉', name: 'Drache', price: 4.99 },
    { id: 'crown', value: '👑', name: 'Krone', price: 4.99 },
    { id: 'skull', value: '💀', name: 'Totenkopf', price: 4.99 },
]

export function ShopScreen() {
    const activeTab = useGameStore(state => state.activeShopTab)
    const setActiveTab = useGameStore(state => state.setShopTab)
    const [bundleTimer, setBundleTimer] = useState(23 * 3600 + 59 * 60 + 59)
    const [showPurchaseModal, setShowPurchaseModal] = useState(false)
    const [selectedPurchase, setSelectedPurchase] = useState<{
        type: 'bundle' | 'gems' | 'energy' | 'vip' | 'vipUpgrade' | 'heroSkin' | 'empireColor' | 'empireEmblem'
        item: any
    } | null>(null)
    const [showSecondChance, setShowSecondChance] = useState(false)
    const [showRewardModal, setShowRewardModal] = useState(false)
    const [lastReward, setLastReward] = useState<{
        title: string
        items: { emoji: string; label: string }[]
        vipSeconds?: number
        isError?: boolean
    } | null>(null)
    const hasPurchased = useRef(false)
    const hasSeenOffer = useRef(false)

    const resources = useGameStore(state => state.resources)
    const isVIP = useGameStore(state => state.isVIP)
    const vipLevel = useGameStore(state => state.vipLevel)
    const vipExpiresAtGameMinute = useGameStore(state => state.vipExpiresAtGameMinute)
    const totalGameMinutes = useGameStore(state => state.totalGameMinutes)
    const heroSkin = useGameStore(state => state.heroSkin)
    const empireColor = useGameStore(state => state.empireColor)
    const empireEmblem = useGameStore(state => state.empireEmblem)
    const ownedHeroSkins = useGameStore(state => state.ownedHeroSkins)
    const ownedEmpireColors = useGameStore(state => state.ownedEmpireColors)
    const ownedEmpireEmblems = useGameStore(state => state.ownedEmpireEmblems)
    const addGems = useGameStore(state => state.addGems)
    const addGold = useGameStore(state => state.addGold)
    const addItem = useGameStore(state => state.addItem)
    const spendGems = useGameStore(state => state.spendGems)
    const spendEuro = useGameStore(state => state.spendEuro)
    const setEnergy = useGameStore(state => state.setEnergy)
    const trackMoneySpent = useGameStore(state => state.trackMoneySpent)
    const activateVIP = useGameStore(state => state.activateVIP)
    const upgradeVIP = useGameStore(state => state.upgradeVIP)
    const setScreen = useGameStore(state => state.setScreen)
    const setCustomization = useGameStore(state => state.setCustomization)
    const unlockHeroSkin = useGameStore(state => state.unlockHeroSkin)
    const unlockEmpireColor = useGameStore(state => state.unlockEmpireColor)
    const unlockEmpireEmblem = useGameStore(state => state.unlockEmpireEmblem)
    const addFreeLootboxSpins = useGameStore(state => state.addFreeLootboxSpins)
    const starterPackLevel = useGameStore(state => state.starterPackLevel)
    const megaBundleLevel = useGameStore(state => state.megaBundleLevel)
    const advanceStarterPack = useGameStore(state => state.advanceStarterPack)
    const advanceMegaBundle = useGameStore(state => state.advanceMegaBundle)
    const battlePassPremium = useGameStore(state => state.battlePassPremium)

    // Tutorial integration
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)

    // Compute active bundles dynamically
    const currentStarter = STARTER_CHAIN[Math.min(starterPackLevel, STARTER_CHAIN.length - 1)]
    // If completed chain (level 10), maybe show nothing or just the last one repeated? User said "10 further packs".
    // We'll show the current level.

    // Mega Bundle logic
    const megaIndex = Math.min(megaBundleLevel, MEGA_LEVELS.length - 1)
    const currentMega = MEGA_LEVELS[megaIndex] // Logic handles infinite on last item

    // Bundle countdown timer
    useEffect(() => {
        const interval = setInterval(() => {
            setBundleTimer(prev => (prev > 0 ? prev - 1 : 23 * 3600 + 59 * 60 + 59))
        }, 1000)
        return () => clearInterval(interval)
    }, [])

    const formatTimer = (seconds: number) => {
        const h = Math.floor(seconds / 3600)
        const m = Math.floor((seconds % 3600) / 60)
        const s = seconds % 60
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    }

    const handlePurchase = (type: 'bundle' | 'gems' | 'energy' | 'vip' | 'vipUpgrade' | 'heroSkin' | 'empireColor' | 'empireEmblem', item: any) => {
        setSelectedPurchase({ type, item })
        setShowPurchaseModal(true)
    }

    const confirmPurchase = () => {
        if (!selectedPurchase || !selectedPurchase.item) return
        const price = selectedPurchase.item.price
        const rewards: { emoji: string; label: string }[] = []

        // Handle different purchase types
        if (selectedPurchase.type === 'bundle') {
            if (!spendEuro(price)) {
                showErrorModal()
                return
            }
            trackMoneySpent(price)

            const item = selectedPurchase.item

            // Mega Bundle Logic
            if (item.label && item.label.includes('MEGA')) {
                // rewards
                addGems(item.gems)
                rewards.push({ emoji: '💎', label: `${item.gems.toLocaleString()} Gems` })

                if (item.vipDays > 0) {
                    activateVIP(item.vipDays * 24)
                    rewards.push({ emoji: '👑', label: `${item.vipDays} Tage VIP Status` })
                }

                // Add Items
                if (item.items > 0) {
                    const armors = [
                        { name: 'Drachenhelm', slot: 'helmet', emoji: '🪖' },
                        { name: 'Drachenrüstung', slot: 'armor', emoji: '🛡️' },
                        { name: 'Drachenstiefel', slot: 'boots', emoji: '👞' }
                    ]
                    for (let i = 0; i < item.items; i++) {
                        const armor = armors[i % armors.length]
                        const power = Math.floor(Math.random() * (100 - 50 + 1)) + 50
                        const id = `mega-armor-${armor.slot}-${Date.now()}-${i}`
                        addItem({
                            id,
                            name: armor.name,
                            emoji: armor.emoji,
                            slot: armor.slot as any,
                            power: power,
                            rarity: 'legendary'
                        })
                        rewards.push({ emoji: armor.emoji, label: `${armor.name} (+${power} Power)` })
                    }
                }

                advanceMegaBundle()
                setLastReward({ title: 'MEGA BUNDLE ERHALTEN!', items: rewards, vipSeconds: item.vipDays * 3600 * 24 })

            } else if (item.item) {
                // Starter Chain Logic (since it has 'item' prop inside)
                addGems(item.gems)
                rewards.push({ emoji: '💎', label: `${item.gems.toLocaleString()} Gems` })
                addGold(item.gold)
                rewards.push({ emoji: '💰', label: `${item.gold.toLocaleString()} Gold` })

                const wItem = item.item
                const id = `starter-item-${Date.now()}`
                addItem({
                    id,
                    name: wItem.name,
                    emoji: wItem.slot === 'weapon' ? '⚔️' : (wItem.slot === 'helmet' ? '🪖' : '🛡️'),
                    slot: wItem.slot as any,
                    power: wItem.power,
                    rarity: wItem.rarity as any
                })
                rewards.push({ emoji: wItem.slot === 'weapon' ? '⚔️' : '🛡️', label: wItem.name })

                advanceStarterPack()
                setLastReward({ title: 'PACK ERHALTEN!', items: rewards })

            } else if ((item as any).rewards) {
                // Regular Bundles
                const b = item as any
                const r = b.rewards
                if (r.gems) {
                    addGems(r.gems)
                    rewards.push({ emoji: '💎', label: `${r.gems} Gems` })
                }
                if (r.gold) {
                    addGold(r.gold)
                    rewards.push({ emoji: '💰', label: `${r.gold} Gold` })
                }
                if (r.energy) {
                    setEnergy(resources.energy + r.energy)
                    rewards.push({ emoji: '⚡', label: `${r.energy} Energie` })
                }
                if (r.lootboxes) {
                    addFreeLootboxSpins(r.lootboxes)
                    rewards.push({ emoji: '🎁', label: `${r.lootboxes} Lootboxen` })
                }
                setLastReward({ title: 'BUNDLE ERHALTEN!', items: rewards })

                // Tutorial: Advance to gacha phase after getting free lootbox
                if (b.tutorialOnly && tutorialPhase === 'shop') {
                    setTimeout(() => {
                        setTutorialPhase('gacha')
                        setScreen('gacha')
                    }, 1500) // Wait for reward modal animation
                }
            }
        } else if (selectedPurchase.type === 'gems') {
            if (!spendEuro(price)) {
                showErrorModal()
                return
            }
            trackMoneySpent(price)
            const pack = selectedPurchase.item as typeof GEM_PACKS[0]
            addGems(pack.gems)
            rewards.push({ emoji: '💎', label: `${pack.gems.toLocaleString()} Gems` })
            setLastReward({ title: 'KAUF ERFOLGREICH!', items: rewards })
        } else if (selectedPurchase.type === 'energy') {
            if (!spendGems(price)) {
                setShowPurchaseModal(false)
                setLastReward({
                    title: '❌ NICHT GENUG GEMS!',
                    items: [{ emoji: '💎', label: 'Du brauchst mehr Gems!' }],
                    isError: true
                })
                setShowRewardModal(true)
                return
            }
            const pack = selectedPurchase.item as typeof ENERGY_PACKS[0]
            const newEnergy = Math.min(resources.energy + pack.energy, resources.maxEnergy)
            setEnergy(newEnergy)
            rewards.push({ emoji: '⚡', label: `${pack.energy} Energie` })
            setLastReward({ title: 'ENERGIE AUFGELADEN!', items: rewards })
        } else if (selectedPurchase.type === 'vip') {
            if (!spendEuro(price)) {
                showErrorModal()
                return
            }
            trackMoneySpent(price)
            const pack = selectedPurchase.item
            activateVIP(pack.duration, pack.level || 1)
            rewards.push({ emoji: getVIPIcon(pack.level || 1), label: pack.name })
            setLastReward({ title: `VIP LEVEL ${pack.level || 1} AKTIVIERT!`, items: rewards, vipSeconds: pack.duration * 3600 })
        } else if (selectedPurchase.type === 'vipUpgrade') {
            if (!spendEuro(price)) {
                showErrorModal()
                return
            }
            trackMoneySpent(price)
            const pack = selectedPurchase.item
            upgradeVIP(pack.level)
            rewards.push({ emoji: getVIPIcon(pack.level), label: `Upgrade auf Level ${pack.level}` })
            setLastReward({ title: `VIP LEVEL ${pack.level} FREIGESCHALTET!`, items: rewards })
        } else if (selectedPurchase.type === 'heroSkin') {
            if (!spendEuro(price)) {
                showErrorModal()
                return
            }
            trackMoneySpent(price)
            const skin = selectedPurchase.item
            unlockHeroSkin(skin.id)
            setCustomization({ heroSkin: skin.id })
            rewards.push({ emoji: '👔', label: skin.name })
            setLastReward({ title: 'HERO SKIN FREIGESCHALTET!', items: rewards })
        } else if (selectedPurchase.type === 'empireColor') {
            if (!spendEuro(price)) {
                showErrorModal()
                return
            }
            trackMoneySpent(price)
            const color = selectedPurchase.item
            unlockEmpireColor(color.id)
            setCustomization({ empireColor: color.value })
            rewards.push({ emoji: '🎨', label: color.name })
            setLastReward({ title: 'FARBE FREIGESCHALTET!', items: rewards })
        } else if (selectedPurchase.type === 'empireEmblem') {
            if (!spendEuro(price)) {
                showErrorModal()
                return
            }
            trackMoneySpent(price)
            const emblem = selectedPurchase.item
            unlockEmpireEmblem(emblem.id)
            setCustomization({ empireEmblem: emblem.value })
            rewards.push({ emoji: emblem.value, label: emblem.name })
            setLastReward({ title: 'WAPPEN FREIGESCHALTET!', items: rewards })
        }

        setShowPurchaseModal(false)
        setSelectedPurchase(null)
        setShowRewardModal(true)
        hasPurchased.current = true
    }

    const showErrorModal = () => {
        setShowPurchaseModal(false)
        setLastReward({
            title: '❌ KEIN GELD!',
            items: [{ emoji: '💶', label: 'Dein Konto ist leer!' }],
            isError: true
        })
        setShowRewardModal(true)
    }

    const handleNavigateAway = (screen: string) => {
        if (!hasPurchased.current && !hasSeenOffer.current) {
            hasSeenOffer.current = true
            setShowSecondChance(true)
        } else {
            setScreen(screen as any)
        }
    }

    const handleSecondChanceClose = () => {
        setShowSecondChance(false)
    }

    return (
        <GameLayout>
            <SecondChanceModal
                trigger="shop_exit"
                isOpen={showSecondChance}
                onClose={handleSecondChanceClose}
            />

            {/* Header */}
            <header className="shop__header">
                <h1 className="shop__title"><span style={{ background: 'none', WebkitTextFillColor: 'initial' }}>🛒</span> PREMIUM SHOP</h1>
                <button className="shop__bank-btn" onClick={() => setScreen('bank')}>
                    🏦 Bank
                </button>
            </header>

            {/* Tab Navigation */}
            <div className="shop__tabs">
                <button
                    className={`shop__tab ${activeTab === 'bundles' ? 'shop__tab--active' : ''}`}
                    onClick={() => setActiveTab('bundles')}
                >
                    Bundles
                </button>
                <button
                    className={`shop__tab ${activeTab === 'packs' ? 'shop__tab--active' : ''}`}
                    onClick={() => setActiveTab('packs')}
                >
                    Packs
                </button>
                <button
                    className={`shop__tab ${activeTab === 'passes' ? 'shop__tab--active' : ''}`}
                    onClick={() => setActiveTab('passes')}
                >
                    Pässe
                </button>
                <button
                    className={`shop__tab ${activeTab === 'skins' ? 'shop__tab--active' : ''}`}
                    onClick={() => setActiveTab('skins')}
                >
                    Skins
                </button>
            </div>

            <div className="shop__scroll">
                {/* BUNDLES TAB */}
                {activeTab === 'bundles' && (
                    <section className="shop__section">

                        {/* Tutorial Welcome Bundle - Full Width at Top */}
                        {tutorialPhase === 'shop' && (
                            <div className="shop__tutorial-welcome" id="tutorial-free-bundle">
                                <div className="shop__tutorial-welcome-badge">🎁 WILLKOMMENSGESCHENK</div>
                                <h2 className="shop__tutorial-welcome-title">Dein Gratis Starterpaket!</h2>

                                <div className="shop__tutorial-welcome-items">
                                    <div className="shop__tutorial-welcome-item">
                                        <span className="shop__tutorial-welcome-emoji">🎁</span>
                                        <span>1 Gratis Lootbox</span>
                                    </div>
                                    <div className="shop__tutorial-welcome-item">
                                        <span className="shop__tutorial-welcome-emoji">✨</span>
                                        <span>Tutorial Spezial</span>
                                    </div>
                                </div>

                                <div className="shop__tutorial-welcome-price">
                                    <span className="shop__tutorial-welcome-original">9.99€</span>
                                    <span className="shop__tutorial-welcome-free">GRATIS!</span>
                                </div>

                                <div className="shop__tutorial-btn-wrapper">
                                    <button
                                        className="btn btn--gold btn--full shop__tutorial-btn"
                                        onClick={() => {
                                            const tutorialBundle = REGULAR_BUNDLES.find(b => b.id === 'tutorial-free')
                                            if (tutorialBundle) handlePurchase('bundle', tutorialBundle)
                                        }}
                                    >
                                        JETZT EINLÖSEN
                                    </button>
                                    <span className="shop__tutorial-hand">👆</span>
                                </div>
                            </div>
                        )}

                        {/* Show other bundles (dimmed during tutorial) */}
                        <div className={tutorialPhase === 'shop' ? 'shop__bundles-dimmed' : ''}>
                            {/* MEGA BUNDLE (DYNAMIC) */}
                            <div className={`shop__bundle shop__bundle--mega shop__bundle--highlight`}>
                                <div className="shop__bundle-badge">{currentMega.infinite ? 'HOT!' : '-80%!'}</div>
                                <h3 className="shop__bundle-name">{currentMega.label}</h3>
                                <div className="shop__bundle-timer">⏰ Nur noch {formatTimer(bundleTimer)}!</div>

                                <div className="shop__bundle-items">
                                    <span className="shop__bundle-item">💎 {currentMega.gems.toLocaleString()} Gems</span>
                                    <span className="shop__bundle-item">👑 {currentMega.vipDays} Tage VIP</span>
                                    {currentMega.gold > 0 && <span className="shop__bundle-item">💰 {currentMega.gold.toLocaleString()} Gold</span>}
                                    {currentMega.items > 0 && <span className="shop__bundle-item">🛡️ {currentMega.items}x Legendäre Items</span>}
                                </div>

                                <div className="shop__bundle-price">
                                    <span className="shop__bundle-original">{(currentMega.price * 1.5).toFixed(2)}€</span>
                                    <span className="shop__bundle-current">{currentMega.price.toFixed(2)}€</span>
                                </div>

                                <button className="btn btn--gold btn--full" onClick={() => handlePurchase('bundle', { ...currentMega, name: currentMega.label })}>
                                    JETZT KAUFEN
                                </button>
                            </div>

                            {/* STARTER CHAIN (DYNAMIC) */}
                            <div className={`shop__bundle shop__bundle--starter`}>
                                <h3 className="shop__bundle-name">⭐ {starterPackLevel === 0 ? 'STARTER PACK' : `ADVANCED PACK ${starterPackLevel}`}</h3>

                                <div className="shop__bundle-items">
                                    <span className="shop__bundle-item">💎 {currentStarter.gems.toLocaleString()} Gems</span>
                                    <span className="shop__bundle-item">💰 {currentStarter.gold.toLocaleString()} Gold</span>
                                    <span className="shop__bundle-item">⚔️ {currentStarter.item.name}</span>
                                </div>

                                <div className="shop__bundle-price">
                                    <span className="shop__bundle-original">{(currentStarter.price * 2).toFixed(2)}€</span>
                                    <span className="shop__bundle-current">{currentStarter.price.toFixed(2)}€</span>
                                </div>

                                <button className="btn btn--gold btn--full" onClick={() => handlePurchase('bundle', { ...currentStarter, name: starterPackLevel === 0 ? 'STARTER PACK' : `ADVANCED PACK ${starterPackLevel}` })}>
                                    JETZT KAUFEN
                                </button>
                                <p className="shop__bundle-note">⚠️ Nur einmal kaufbar!</p>
                            </div>

                            <div className="shop__bundles-grid">
                                {REGULAR_BUNDLES
                                    .filter(bundle => {
                                        // Always hide tutorial-only bundles from grid (handled by dedicated section)
                                        return (bundle as any).tutorialOnly !== true
                                    })
                                    .map(bundle => (
                                        <div
                                            key={bundle.id}
                                            className={`shop__bundle shop__bundle--regular shop__bundle--${bundle.rarity}`}
                                        >
                                            {(bundle as any).tutorialOnly && (
                                                <div className="shop__bundle-free-badge">🎁 GRATIS</div>
                                            )}
                                            <h3 className="shop__bundle-name" style={{ fontSize: '1.2rem', marginBottom: '10px' }}>{bundle.name}</h3>

                                            <div className="shop__bundle-items">
                                                {bundle.items.map((item, idx) => (
                                                    <div key={idx} className="shop__bundle-item">
                                                        <span>{item.emoji}</span>
                                                        <span>{item.label}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="shop__bundle-price">
                                                <span className="shop__bundle-original">{(bundle as any).originalPrice}€</span>
                                                <span className="shop__bundle-current">
                                                    {bundle.price === 0 ? 'GRATIS!' : `${bundle.price.toFixed(2)}€`}
                                                </span>
                                            </div>

                                            <button
                                                className="btn btn--gold btn--full"
                                                onClick={() => handlePurchase('bundle', bundle)}
                                            >
                                                {bundle.price === 0 ? 'Einlösen' : 'Kaufen'}
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* PACKS TAB */}
                {activeTab === 'packs' && (
                    <>
                        <section className="shop__section">
                            <h2 className="shop__section-title">💎 GEM PACKS</h2>
                            <div className="shop__gems-grid">
                                {GEM_PACKS.map((pack, idx) => (
                                    <div
                                        key={idx}
                                        className={`shop__gem-pack ${pack.popular ? 'shop__gem-pack--popular' : ''}`}
                                        onClick={() => handlePurchase('gems', pack)}
                                    >
                                        {pack.popular && (
                                            <div className="shop__gem-pack-badge">🔥 BELIEBT</div>
                                        )}
                                        {pack.bonus && (
                                            <div className="shop__gem-pack-bonus">{pack.bonus}</div>
                                        )}
                                        <span className="shop__gem-pack-emoji">💎</span>
                                        <span className="shop__gem-pack-amount">{pack.gems.toLocaleString()}</span>
                                        <span className="shop__gem-pack-price">{pack.price.toFixed(2)}€</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="shop__section">
                            <h2 className="shop__section-title">⚡ ENERGY PACKS</h2>
                            <div className="shop__gems-grid">
                                {ENERGY_PACKS.map((pack, idx) => (
                                    <div
                                        key={idx}
                                        className={`shop__energy-pack ${pack.popular ? 'shop__energy-pack--popular' : ''}`}
                                        onClick={() => handlePurchase('energy', pack)}
                                    >
                                        {pack.popular && (
                                            <div className="shop__energy-pack-badge">🔥 BELIEBT</div>
                                        )}
                                        {pack.bonus && (
                                            <div className="shop__energy-pack-bonus">{pack.bonus}</div>
                                        )}
                                        <span className="shop__energy-pack-emoji">⚡</span>
                                        <span className="shop__energy-pack-amount">{pack.energy.toLocaleString()}</span>
                                        <span className="shop__energy-pack-price">{pack.price} 💎</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </>
                )}

                {/* PASSES TAB */}
                {activeTab === 'passes' && (
                    <>


                        {/* VIP PASS - Show base or upgrades based on current level */}
                        {!isVIP ? (
                            <section className="shop__vip">
                                <div className="shop__vip-header">
                                    <span className="shop__vip-crown">👑</span>
                                    <h2 className="shop__vip-title">VIP PASS</h2>
                                </div>

                                <div className="shop__vip-perks">
                                    <span className="shop__vip-perk">✅ 2. Bauslot (statt 1)</span>
                                    <span className="shop__vip-perk">✅ Auto-Kampf System</span>
                                    <span className="shop__vip-perk">✅ +50 Max. Energie</span>
                                </div>

                                <div className="shop__vip-cards">
                                    <div
                                        className="shop__vip-card"
                                        onClick={() => handlePurchase('vip', { name: 'VIP Pass Level 1 (1 Tag)', price: GAME_CONFIG.pricing.vip1Day, duration: 24, level: 1 })}
                                    >
                                        <span className="shop__vip-card-duration">1 Tag</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip1Day.toFixed(2)}€</span>
                                    </div>

                                    <div
                                        className="shop__vip-card shop__vip-card--popular"
                                        onClick={() => handlePurchase('vip', { name: 'VIP Pass Level 1 (7 Tage)', price: GAME_CONFIG.pricing.vip7Days, duration: 168, level: 1 })}
                                    >
                                        <span className="shop__vip-card-badge">-43%</span>
                                        <span className="shop__vip-card-duration">7 Tage</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip7Days.toFixed(2)}€</span>
                                    </div>

                                    <div
                                        className="shop__vip-card shop__vip-card--best"
                                        onClick={() => handlePurchase('vip', { name: 'VIP Pass Level 1 (30 Tage)', price: GAME_CONFIG.pricing.vip30Days, duration: 720, level: 1 })}
                                    >
                                        <span className="shop__vip-card-badge">-67%</span>
                                        <span className="shop__vip-card-duration">30 Tage</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip30Days.toFixed(2)}€</span>
                                    </div>
                                </div>
                            </section>
                        ) : vipLevel === 1 ? (
                            <section className="shop__vip shop__vip--upgrade">
                                <div className="shop__vip-header">
                                    <span className="shop__vip-crown">👑2</span>
                                    <h2 className="shop__vip-title">UPGRADE TO VIP LEVEL 2</h2>
                                </div>

                                <div className="shop__vip-upgrade-info">
                                    <p className="shop__vip-upgrade-desc">Erweitere deine VIP-Vorteile und hebe dein Imperium auf das nächste Level!</p>
                                </div>

                                <div className="shop__vip-perks">
                                    <span className="shop__vip-perk shop__vip-perk--new">✨ 3. Bauslot</span>
                                    <span className="shop__vip-perk shop__vip-perk--new">✨ +100 Zusätzliche Max. Energie</span>
                                    <span className="shop__vip-perk shop__vip-perk--new">✨ Premium VIP Badge 👑2</span>
                                </div>

                                <div className="shop__vip-cards">
                                    <div
                                        className="shop__vip-card"
                                        onClick={() => handlePurchase('vipUpgrade', { name: 'VIP Level 2 Upgrade (1 Tag)', price: GAME_CONFIG.pricing.vip1Day, duration: 24, level: 2 })}
                                    >
                                        <span className="shop__vip-card-duration">1 Tag</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip1Day.toFixed(2)}€</span>
                                    </div>

                                    <div
                                        className="shop__vip-card shop__vip-card--popular"
                                        onClick={() => handlePurchase('vipUpgrade', { name: 'VIP Level 2 Upgrade (7 Tage)', price: GAME_CONFIG.pricing.vip7Days, duration: 168, level: 2 })}
                                    >
                                        <span className="shop__vip-card-badge">-43%</span>
                                        <span className="shop__vip-card-duration">7 Tage</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip7Days.toFixed(2)}€</span>
                                    </div>

                                    <div
                                        className="shop__vip-card shop__vip-card--best"
                                        onClick={() => handlePurchase('vipUpgrade', { name: 'VIP Level 2 Upgrade (30 Tage)', price: GAME_CONFIG.pricing.vip30Days, duration: 720, level: 2 })}
                                    >
                                        <span className="shop__vip-card-badge">-67%</span>
                                        <span className="shop__vip-card-duration">30 Tage</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip30Days.toFixed(2)}€</span>
                                    </div>
                                </div>
                            </section>
                        ) : vipLevel === 2 ? (
                            <section className="shop__vip shop__vip--upgrade">
                                <div className="shop__vip-header">
                                    <span className="shop__vip-crown">👑3</span>
                                    <h2 className="shop__vip-title">UPGRADE TO VIP LEVEL 3</h2>
                                </div>

                                <div className="shop__vip-upgrade-info">
                                    <p className="shop__vip-upgrade-desc">Steige auf in die Elite und erlebe die ultimativen VIP-Vorteile!</p>
                                </div>

                                <div className="shop__vip-perks">
                                    <span className="shop__vip-perk shop__vip-perk--new">✨ 4. Bauslot</span>
                                    <span className="shop__vip-perk shop__vip-perk--new">✨ +150 Zusätzliche Max. Energie</span>
                                    <span className="shop__vip-perk shop__vip-perk--new">✨ Elite VIP Badge 👑3</span>
                                </div>

                                <div className="shop__vip-cards">
                                    <div
                                        className="shop__vip-card"
                                        onClick={() => handlePurchase('vipUpgrade', { name: 'VIP Level 3 Upgrade (1 Tag)', price: GAME_CONFIG.pricing.vip1Day, duration: 24, level: 3 })}
                                    >
                                        <span className="shop__vip-card-duration">1 Tag</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip1Day.toFixed(2)}€</span>
                                    </div>

                                    <div
                                        className="shop__vip-card shop__vip-card--popular"
                                        onClick={() => handlePurchase('vipUpgrade', { name: 'VIP Level 3 Upgrade (7 Tage)', price: GAME_CONFIG.pricing.vip7Days, duration: 168, level: 3 })}
                                    >
                                        <span className="shop__vip-card-badge">-43%</span>
                                        <span className="shop__vip-card-duration">7 Tage</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip7Days.toFixed(2)}€</span>
                                    </div>

                                    <div
                                        className="shop__vip-card shop__vip-card--best"
                                        onClick={() => handlePurchase('vipUpgrade', { name: 'VIP Level 3 Upgrade (30 Tage)', price: GAME_CONFIG.pricing.vip30Days, duration: 720, level: 3 })}
                                    >
                                        <span className="shop__vip-card-badge">-67%</span>
                                        <span className="shop__vip-card-duration">30 Tage</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip30Days.toFixed(2)}€</span>
                                    </div>
                                </div>
                            </section>
                        ) : (
                            <section className="shop__vip shop__vip--maxlevel">
                                <div className="shop__vip-header">
                                    <span className="shop__vip-crown">👑3</span>
                                    <h2 className="shop__vip-title">ELITE VIP - MAX LEVEL</h2>
                                </div>
                                <div className="shop__vip-upgrade-info">
                                    <p className="shop__vip-upgrade-current">Du hast das höchste VIP-Level erreicht!</p>
                                    {vipExpiresAtGameMinute && (
                                        <p className="shop__vip-upgrade-desc">
                                            Verbleibende Zeit: {Math.floor((vipExpiresAtGameMinute - totalGameMinutes) / (24 * 60))} Tage {Math.floor(((vipExpiresAtGameMinute - totalGameMinutes) % (24 * 60)) / 60)} Std
                                        </p>
                                    )}
                                </div>

                                <div className="shop__vip-perks">
                                    <span className="shop__vip-perk">Verlängere deinen Elite-VIP Status:</span>
                                </div>

                                <div className="shop__vip-cards">
                                    <div
                                        className="shop__vip-card"
                                        onClick={() => handlePurchase('vip', { name: 'VIP Zeit Verlängerung (1 Tag)', price: GAME_CONFIG.pricing.vip1Day, duration: 24, level: 3 })}
                                    >
                                        <span className="shop__vip-card-duration">1 Tag</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip1Day.toFixed(2)}€</span>
                                    </div>

                                    <div
                                        className="shop__vip-card shop__vip-card--popular"
                                        onClick={() => handlePurchase('vip', { name: 'VIP Zeit Verlängerung (7 Tage)', price: GAME_CONFIG.pricing.vip7Days, duration: 168, level: 3 })}
                                    >
                                        <span className="shop__vip-card-badge">-43%</span>
                                        <span className="shop__vip-card-duration">7 Tage</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip7Days.toFixed(2)}€</span>
                                    </div>

                                    <div
                                        className="shop__vip-card shop__vip-card--best"
                                        onClick={() => handlePurchase('vip', { name: 'VIP Zeit Verlängerung (30 Tage)', price: GAME_CONFIG.pricing.vip30Days, duration: 720, level: 3 })}
                                    >
                                        <span className="shop__vip-card-badge">-67%</span>
                                        <span className="shop__vip-card-duration">30 Tage</span>
                                        <span className="shop__vip-card-price">{GAME_CONFIG.pricing.vip30Days.toFixed(2)}€</span>
                                    </div>
                                </div>
                            </section>
                        )}
                        {!battlePassPremium && (
                            <section className="shop__battlepass">
                                <div className="shop__battlepass-header">
                                    <span className="shop__battlepass-icon">⚔️</span>
                                    <div>
                                        <h2 className="shop__battlepass-title">BATTLE PASS</h2>
                                        <span className="shop__battlepass-season">Season 1: Legends of Greed</span>
                                    </div>
                                </div>
                                <div className="shop__battlepass-content">
                                    <div className="shop__battlepass-benefits">
                                        <span>👑 50 Premium Belohnungen</span>
                                        <span>🌟 Exklusiver Season-Skin</span>
                                        <span>💎 ~5000 Gems Wert</span>
                                    </div>
                                    <div className="shop__battlepass-price">
                                        <span className="shop__battlepass-price-value">{GAME_CONFIG.pricing.battlePassPremium.toFixed(2)}€</span>
                                    </div>
                                    <button
                                        className="btn btn--gold btn--full"
                                        onClick={() => setScreen('battlepass')}
                                    >
                                        Zum Battle Pass →
                                    </button>
                                </div>
                            </section>
                        )}
                    </>
                )}

                {/* SKINS TAB */}
                {activeTab === 'skins' && (
                    <>
                        {/* Hero Skins */}
                        <section className="shop__section">
                            <h2 className="shop__section-title">👔 HERO SKINS</h2>
                            <div className="shop__skins-grid">
                                {HERO_SKINS.map(skin => {
                                    const isOwned = ownedHeroSkins.includes(skin.id)
                                    const isActive = heroSkin === skin.id
                                    return (
                                        <div
                                            key={skin.id}
                                            className={`shop__skin-card shop__skin-card--${skin.rarity} ${isOwned ? 'shop__skin-card--owned' : ''} ${isActive ? 'shop__skin-card--active' : ''}`}
                                            onClick={() => {
                                                if (isOwned && !isActive) {
                                                    setCustomization({ heroSkin: skin.id })
                                                } else if (!isOwned && skin.price > 0) {
                                                    handlePurchase('heroSkin', skin)
                                                }
                                            }}
                                        >
                                            <div className="shop__skin-preview">
                                                <img src={skin.image} alt={skin.name} />
                                            </div>
                                            <div className="shop__skin-info">
                                                <h3 className="shop__skin-name">{skin.name}</h3>
                                                <span className={`shop__skin-rarity-badge rarity-badge--${skin.rarity}`}>
                                                    {skin.rarity}
                                                </span>
                                                {!isOwned && skin.price > 0 && (
                                                    <span className="shop__skin-price">{skin.price.toFixed(2)}€</span>
                                                )}
                                                {isOwned && !isActive && (
                                                    <span className="shop__skin-action">Aktivieren</span>
                                                )}
                                                {isActive && (
                                                    <span className="shop__skin-active">✓ Aktiv</span>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </section>

                        {/* Empire Colors */}
                        <section className="shop__section">
                            <h2 className="shop__section-title">🎨 REICH FARBEN</h2>
                            <div className="shop__emblems-grid">
                                {EMPIRE_COLORS.map(color => {
                                    const isOwned = ownedEmpireColors.includes(color.id)
                                    const isActive = empireColor === color.value
                                    return (
                                        <div
                                            key={color.id}
                                            className={`shop__emblem-card ${isOwned ? 'shop__emblem-card--owned' : ''} ${isActive ? 'shop__emblem-card--active' : ''}`}
                                            onClick={() => {
                                                if (isOwned && !isActive) {
                                                    setCustomization({ empireColor: color.value })
                                                } else if (!isOwned && color.price > 0) {
                                                    handlePurchase('empireColor', color)
                                                }
                                            }}
                                        >
                                            <div className="shop__emblem-preview" style={{ backgroundColor: color.value }}>
                                                {isActive && <span className="shop__emblem-check">✓</span>}
                                            </div>
                                            <div className="shop__emblem-info">
                                                <h4 className="shop__emblem-name">{color.name}</h4>
                                                {!isOwned && color.price > 0 && (
                                                    <span className="shop__emblem-price">{color.price.toFixed(2)}€</span>
                                                )}
                                                {isOwned && !isActive && (
                                                    <span className="shop__emblem-action">Aktivieren</span>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </section>

                        {/* Empire Emblems */}
                        <section className="shop__section">
                            <h2 className="shop__section-title">🛡️ REICH WAPPEN</h2>
                            <div className="shop__emblems-grid">
                                {EMPIRE_EMBLEMS.map(emblem => {
                                    const isOwned = ownedEmpireEmblems.includes(emblem.id)
                                    const isActive = empireEmblem === emblem.value
                                    return (
                                        <div
                                            key={emblem.id}
                                            className={`shop__emblem-card ${isOwned ? 'shop__emblem-card--owned' : ''} ${isActive ? 'shop__emblem-card--active' : ''}`}
                                            onClick={() => {
                                                if (isOwned && !isActive) {
                                                    setCustomization({ empireEmblem: emblem.value })
                                                } else if (!isOwned && emblem.price > 0) {
                                                    handlePurchase('empireEmblem', emblem)
                                                }
                                            }}
                                        >
                                            <div className="shop__emblem-preview shop__emblem-preview--emoji">
                                                <span className="shop__emblem-emoji">{emblem.value}</span>
                                                {isActive && <span className="shop__emblem-check">✓</span>}
                                            </div>
                                            <div className="shop__emblem-info">
                                                <h4 className="shop__emblem-name">{emblem.name}</h4>
                                                {!isOwned && emblem.price > 0 && (
                                                    <span className="shop__emblem-price">{emblem.price.toFixed(2)}€</span>
                                                )}
                                                {isOwned && !isActive && (
                                                    <span className="shop__emblem-action">Aktivieren</span>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </section>
                    </>
                )}
            </div>

            {/* Purchase Confirmation Modal */}
            {showPurchaseModal && selectedPurchase && (
                <div className="modal-overlay" onClick={() => setShowPurchaseModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal__header">
                            <h2>💳 Kauf bestätigen</h2>
                        </div>

                        <div className="modal__content">
                            <p className="shop__confirm-text">
                                {selectedPurchase.type === 'bundle'
                                    ? `${(selectedPurchase.item as any).name}`
                                    : selectedPurchase.type === 'gems'
                                        ? `💎 ${(selectedPurchase.item as typeof GEM_PACKS[0]).gems.toLocaleString()} Gems`
                                        : selectedPurchase.type === 'energy'
                                            ? `⚡ ${(selectedPurchase.item as typeof ENERGY_PACKS[0]).energy.toLocaleString()} Energie`
                                            : selectedPurchase.type === 'heroSkin'
                                                ? `👔 ${selectedPurchase.item.name}`
                                                : selectedPurchase.type === 'empireColor'
                                                    ? `🎨 ${selectedPurchase.item.name}`
                                                    : selectedPurchase.type === 'empireEmblem'
                                                        ? `${selectedPurchase.item.value} ${selectedPurchase.item.name}`
                                                        : (selectedPurchase.item as any).name
                                }
                            </p>
                            <div className={`shop__confirm-price-row ${(selectedPurchase?.item?.price ?? 0) > (selectedPurchase.type === 'energy' ? resources.gems : resources.euro) ? 'text-danger' : ''}`}>
                                <span className="shop__confirm-price-label">Preis:</span>
                                <span className="shop__confirm-price">
                                    {selectedPurchase.type === 'energy'
                                        ? `${(selectedPurchase?.item?.price ?? 0)} 💎`
                                        : `${(selectedPurchase?.item?.price ?? 0).toFixed(2)}€`
                                    }
                                </span>
                            </div>
                            {selectedPurchase.type !== 'energy' && (
                                <p className="shop__confirm-note">
                                    (Simulierter Kauf - kein echtes Geld!)
                                </p>
                            )}
                            {((selectedPurchase.type === 'energy' && resources.gems < (selectedPurchase?.item?.price ?? 0)) ||
                                (selectedPurchase.type !== 'energy' && resources.euro < (selectedPurchase?.item?.price ?? 0))) && (
                                    <div className="shop__confirm-error-box">
                                        <p className="shop__confirm-error">⚠️ Nicht genügend Guthaben!</p>
                                        <button className="btn btn--gold btn--sm w-full" onClick={() => setScreen(selectedPurchase.type === 'energy' ? 'shop' : 'bank')}>
                                            {selectedPurchase.type === 'energy' ? '💎 Gems kaufen' : '🏦 Zum Banking'}
                                        </button>
                                    </div>
                                )}
                        </div>

                        <div className="modal__actions">
                            <button
                                className="btn btn--gold btn--full"
                                onClick={confirmPurchase}
                            >
                                💸 Kaufen
                            </button>
                            <button
                                className="btn btn--ghost btn--full"
                                onClick={() => setShowPurchaseModal(false)}
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reward Confirmation Modal */}
            {showRewardModal && lastReward && (
                <div className="modal-overlay" onClick={() => setShowRewardModal(false)}>
                    <div className="modal modal--reward" onClick={e => e.stopPropagation()}>
                        <div className="modal__header">
                            <h2>✨ {lastReward.title}</h2>
                        </div>
                        <div className="modal__content">
                            <div className="shop__reward-list">
                                {lastReward.items.map((item, idx) => (
                                    <div key={idx} className="shop__reward-item">
                                        <span className="shop__reward-emoji">{item.emoji}</span>
                                        <span className="shop__reward-label">{item.label}</span>
                                    </div>
                                ))}
                            </div>

                            {lastReward.vipSeconds && (
                                <div className="shop__vip-info-box">
                                    <h3>👑 VIP VORTEILE AKTIV:</h3>
                                    <ul>
                                        <li>🚀 2. Bauslot freigeschaltet</li>
                                        <li>🤖 Auto-Kampf System online</li>
                                        <li>⚡ +50 Maximale Energie</li>
                                    </ul>
                                    <p className="shop__vip-expiry">
                                        Gesamte VIP Zeit: {Math.max(0, Math.ceil(((vipExpiresAtGameMinute || 0) - totalGameMinutes) / 60))} Stunden verbleibend
                                    </p>
                                </div>
                            )}

                            {lastReward.isError && (
                                <div className="shop__error-box">
                                    <p>Du hast nicht genug Euro für diesen Kauf.</p>
                                    <button className="btn btn--primary btn--lg w-full" onClick={() => { setShowRewardModal(false); setScreen('bank'); }}>
                                        🏦 Online Banking öffnen
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="modal__actions">
                            <button
                                className="btn btn--gold btn--full"
                                onClick={() => setShowRewardModal(false)}
                            >
                                {lastReward.isError ? 'SCHADE...' : 'GEIL!'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </GameLayout>
    )
}
